import { useState } from "react";
import {
  startOfMonth,
  endOfMonth,
  addMonths,
  subMonths,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isToday,
  isSameMonth,
  format,
  parseISO,
  isValid,
} from "date-fns";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import {
  ChevronLeft,
  ChevronRight,
  Plane,
  LogOut,
  Clock,
  User,
  Phone,
  Mail,
  MapPin,
  Luggage,
  CreditCard,
  Users,
} from "lucide-react";

interface BookingsCalendarProps {
  rows: any[];
}

const statusColors: Record<string, string> = {
  pending: "bg-yellow-500/15 text-yellow-700 border-yellow-200",
  confirmed: "bg-green-500/15 text-green-700 border-green-200",
  cancelled: "bg-red-500/15 text-red-700 border-red-200",
};

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function getCalendarDays(month: Date) {
  const start = startOfWeek(startOfMonth(month));
  const end = endOfWeek(endOfMonth(month));
  return eachDayOfInterval({ start, end });
}

function parseDateOnly(value?: string | null): Date | null {
  if (!value) return null;
  // Handle 'YYYY-MM-DD' or full ISO. Treat as local date to avoid TZ shifts.
  const datePart = value.length >= 10 ? value.slice(0, 10) : value;
  const d = parseISO(datePart);
  return isValid(d) ? d : null;
}

function isSameLocalDay(value: string | null | undefined, day: Date) {
  const d = parseDateOnly(value);
  if (!d) return false;
  return (
    d.getFullYear() === day.getFullYear() &&
    d.getMonth() === day.getMonth() &&
    d.getDate() === day.getDate()
  );
}

function getBookingsForDay(day: Date, bookings: any[]) {
  const arrivals = bookings.filter((b) => isSameLocalDay(b.arrival_date, day));
  const departures = bookings.filter(
    (b) => b.return_date && isSameLocalDay(b.return_date, day)
  );
  return { arrivals, departures };
}

function DetailRow({
  label,
  value,
  icon,
}: {
  label: string;
  value: React.ReactNode;
  icon?: React.ReactNode;
}) {
  if (value === null || value === undefined || value === "" || value === "—") return null;
  return (
    <div className="flex items-start gap-2 py-1.5 border-b border-border/50 last:border-0">
      {icon && <span className="mt-0.5 text-muted-foreground">{icon}</span>}
      <div className="min-w-0">
        <div className="text-[11px] text-muted-foreground uppercase tracking-wide">
          {label}
        </div>
        <div className="text-sm font-medium break-words">{value}</div>
      </div>
    </div>
  );
}

export function BookingsCalendar({ rows }: BookingsCalendarProps) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);
  const [selectedBooking, setSelectedBooking] = useState<any | null>(null);

  const days = getCalendarDays(currentMonth);
  const selectedDayData = selectedDay ? getBookingsForDay(selectedDay, rows) : null;

  return (
    <div>
      {/* Month navigation */}
      <div className="flex items-center justify-between mb-4">
        <Button
          variant="outline"
          size="icon"
          onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <h3 className="text-lg font-semibold">
          {format(currentMonth, "MMMM yyyy")}
        </h3>
        <Button
          variant="outline"
          size="icon"
          onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {/* Calendar grid */}
      <Card>
        <CardContent className="p-2 sm:p-4">
          <div className="grid grid-cols-7 gap-1">
            {WEEKDAYS.map((d) => (
              <div
                key={d}
                className="text-center text-xs font-semibold text-muted-foreground py-2"
              >
                {d}
              </div>
            ))}

            {days.map((day) => {
              const inMonth = isSameMonth(day, currentMonth);
              const today = isToday(day);
              const { arrivals, departures } = getBookingsForDay(day, rows);
              const hasArrivals = arrivals.length > 0;
              const hasDepartures = departures.length > 0;
              const hasEvents = hasArrivals || hasDepartures;

              let cellBg = "";
              if (hasArrivals && hasDepartures)
                cellBg = "bg-purple-100 dark:bg-purple-900/30";
              else if (hasArrivals)
                cellBg = "bg-purple-50 dark:bg-purple-900/20";
              else if (hasDepartures)
                cellBg = "bg-green-50 dark:bg-green-900/20";

              return (
                <button
                  key={day.toISOString()}
                  onClick={() => hasEvents && setSelectedDay(day)}
                  disabled={!hasEvents}
                  className={`
                    relative min-h-[70px] sm:min-h-[85px] rounded-md border p-1 text-left transition-all
                    ${inMonth ? "" : "opacity-30"}
                    ${today ? "ring-2 ring-primary" : ""}
                    ${cellBg}
                    ${hasEvents ? "cursor-pointer hover:ring-2 hover:ring-primary/50" : "cursor-default"}
                  `}
                >
                  <span
                    className={`text-xs font-medium ${today ? "text-primary font-bold" : "text-foreground"}`}
                  >
                    {format(day, "d")}
                  </span>
                  {hasArrivals && (
                    <div className="flex items-center gap-0.5 mt-0.5">
                      <Plane className="h-3 w-3 text-purple-600 dark:text-purple-400" />
                      <span className="text-[10px] font-semibold text-purple-700 dark:text-purple-300">
                        {arrivals.length}
                      </span>
                    </div>
                  )}
                  {hasDepartures && (
                    <div className="flex items-center gap-0.5 mt-0.5">
                      <LogOut className="h-3 w-3 text-green-600 dark:text-green-400" />
                      <span className="text-[10px] font-semibold text-green-700 dark:text-green-300">
                        {departures.length}
                      </span>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-muted-foreground">
        <div className="flex items-center gap-1.5">
          <div className="h-3 w-3 rounded-sm bg-purple-400" />
          <span>Llegadas (Arrivals)</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="h-3 w-3 rounded-sm bg-green-400" />
          <span>Salidas (Departures)</span>
        </div>
      </div>

      {/* Day detail dialog */}
      <Dialog
        open={!!selectedDay && !selectedBooking}
        onOpenChange={(open) => !open && setSelectedDay(null)}
      >
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedDay && format(selectedDay, "EEEE, MMMM d, yyyy")}
            </DialogTitle>
          </DialogHeader>

          {selectedDayData && (
            <div className="space-y-4">
              {selectedDayData.arrivals.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-purple-700 dark:text-purple-300 mb-2 flex items-center gap-1">
                    <Plane className="h-4 w-4" /> Llegadas (
                    {selectedDayData.arrivals.length})
                  </h4>
                  <div className="space-y-2">
                    {selectedDayData.arrivals.map((b: any) => (
                      <BookingCard
                        key={b.id}
                        booking={b}
                        type="arrival"
                        onClick={() => setSelectedBooking(b)}
                      />
                    ))}
                  </div>
                </div>
              )}
              {selectedDayData.departures.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-green-700 dark:text-green-300 mb-2 flex items-center gap-1">
                    <LogOut className="h-4 w-4" /> Salidas (
                    {selectedDayData.departures.length})
                  </h4>
                  <div className="space-y-2">
                    {selectedDayData.departures.map((b: any) => (
                      <BookingCard
                        key={b.id}
                        booking={b}
                        type="departure"
                        onClick={() => setSelectedBooking(b)}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Close</Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Booking detail dialog */}
      <Dialog
        open={!!selectedBooking}
        onOpenChange={(open) => !open && setSelectedBooking(null)}
      >
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-primary">
                {selectedBooking?.booking_number || "—"}
              </span>
              {selectedBooking && (
                <Badge
                  variant="outline"
                  className={statusColors[selectedBooking.booking_status] || ""}
                >
                  {selectedBooking.booking_status}
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>

          {selectedBooking && (
            <div className="space-y-1">
              <DetailRow
                label="Customer"
                value={selectedBooking.full_name}
                icon={<User className="h-3.5 w-3.5" />}
              />
              <DetailRow
                label="Phone"
                value={selectedBooking.phone}
                icon={<Phone className="h-3.5 w-3.5" />}
              />
              <DetailRow
                label="Email"
                value={selectedBooking.email}
                icon={<Mail className="h-3.5 w-3.5" />}
              />
              <DetailRow
                label="From"
                value={selectedBooking.from_location}
                icon={<MapPin className="h-3.5 w-3.5" />}
              />
              <DetailRow
                label="To"
                value={selectedBooking.to_location}
                icon={<MapPin className="h-3.5 w-3.5" />}
              />
              <DetailRow
                label="Arrival Date"
                value={
                  selectedBooking.arrival_date
                    ? `${selectedBooking.arrival_date}${selectedBooking.arrival_time ? " · " + selectedBooking.arrival_time : ""}`
                    : null
                }
                icon={<Clock className="h-3.5 w-3.5" />}
              />
              <DetailRow
                label="Airline / Flight"
                value={
                  [selectedBooking.airline, selectedBooking.flight_number]
                    .filter(Boolean)
                    .join(" · ") || null
                }
                icon={<Plane className="h-3.5 w-3.5" />}
              />
              {selectedBooking.return_date && (
                <DetailRow
                  label="Return Date"
                  value={`${selectedBooking.return_date}${selectedBooking.return_time ? " · " + selectedBooking.return_time : ""}`}
                  icon={<Clock className="h-3.5 w-3.5" />}
                />
              )}
              {(selectedBooking.return_airline ||
                selectedBooking.return_flight_number) && (
                <DetailRow
                  label="Return Airline / Flight"
                  value={[
                    selectedBooking.return_airline,
                    selectedBooking.return_flight_number,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                  icon={<Plane className="h-3.5 w-3.5" />}
                />
              )}
              <DetailRow
                label="Passengers"
                value={selectedBooking.passengers}
                icon={<Users className="h-3.5 w-3.5" />}
              />
              <DetailRow
                label="Luggage"
                value={selectedBooking.amount_of_luggage}
                icon={<Luggage className="h-3.5 w-3.5" />}
              />
              <DetailRow
                label="Trip / Service"
                value={[selectedBooking.trip_type, selectedBooking.service_type]
                  .filter(Boolean)
                  .join(" · ")}
              />
              <DetailRow
                label="Payment"
                value={`${selectedBooking.payment_method} · ${selectedBooking.payment_status}`}
                icon={<CreditCard className="h-3.5 w-3.5" />}
              />
              <DetailRow
                label="Total"
                value={
                  <span className="text-green-700 font-bold">
                    ${Number(selectedBooking.total_price || 0).toFixed(2)}
                  </span>
                }
              />
              {selectedBooking.remarks && (
                <DetailRow label="Remarks" value={selectedBooking.remarks} />
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedBooking(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function BookingCard({
  booking,
  type,
  onClick,
}: {
  booking: any;
  type: "arrival" | "departure";
  onClick: () => void;
}) {
  const isCancelled = booking.booking_status === "cancelled";
  const time =
    type === "arrival" ? booking.arrival_time : booking.return_time;
  const airline =
    type === "arrival" ? booking.airline : booking.return_airline;
  const flightNo =
    type === "arrival"
      ? booking.flight_number
      : booking.return_flight_number;

  return (
    <div
      onClick={onClick}
      className={`rounded-lg border p-3 space-y-1 text-sm cursor-pointer hover:ring-2 hover:ring-primary/50 transition-all ${isCancelled ? "opacity-50" : ""}`}
    >
      <div className="flex items-center justify-between flex-wrap gap-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono text-xs font-bold text-primary">
            {booking.booking_number || "—"}
          </span>
          {time && (
            <Badge
              variant="outline"
              className={
                type === "arrival"
                  ? "bg-purple-500/10 text-purple-700 border-purple-200"
                  : "bg-green-500/10 text-green-700 border-green-200"
              }
            >
              <Clock className="h-3 w-3 mr-1" />
              {time}
            </Badge>
          )}
        </div>
        <Badge
          variant="outline"
          className={statusColors[booking.booking_status] || ""}
        >
          {booking.booking_status}
        </Badge>
      </div>
      <div
        className={`font-medium ${isCancelled ? "line-through decoration-red-400" : ""}`}
      >
        {booking.full_name}
      </div>
      <div className="text-xs text-muted-foreground">
        <span>{booking.from_location}</span>
        <span> → </span>
        <span>{booking.to_location}</span>
      </div>
      {(airline || flightNo) && (
        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          <Plane className="h-3 w-3" />
          <span>Vuelo: {[airline, flightNo].filter(Boolean).join(" ")}</span>
        </div>
      )}
      <div className="flex items-center justify-between pt-1">
        <span className="text-xs text-muted-foreground capitalize">
          {booking.service_type || "—"}
        </span>
        <span className="font-semibold text-green-700">
          ${Number(booking.total_price || 0).toFixed(2)}
        </span>
      </div>
    </div>
  );
}
